'use client';

import { Fragment } from 'react';
import { X } from 'lucide-react';
import type { GridMatch } from '@/types/tournament.model';
import { formatDateForUser } from '@/utils/formatters';
import {
  cellKey,
  type GridModel,
  type GridSlotRef,
  type PlaceholderLabelMap,
  type TournamentBadge,
} from './grid-model';
import { dropVerdictFor } from './drop-state';
import { GridCell } from './GridCell';
import { GridAddTimeRow } from './GridAddTimeRow';

interface GridMatrixProps {
  model: GridModel;
  badgeOf: (id: string) => TournamentBadge;
  pendingMatchIds: Set<string>;
  /** True while an unsaved drag-and-drop draft is pending anywhere in the grid. */
  editDisabled: boolean;
  onEditMatch: (match: GridMatch) => void;
  /** matchId -> qualifier labels for its undecided bracket slots. */
  placeholderLabels: PlaceholderLabelMap;
  /** The match currently being dragged, or `null` when nothing is dragging. */
  dragging: GridMatch | null;
  /** Adds a row for a time that no block declares and no match occupies. */
  onAddTime: (time: string) => void;
  /** Takes a row out of the view. Only offered for rows with no match on any date. */
  onRemoveTime: (time: string) => void;
  /** Opens the "who can play" list for a slot. */
  onShowAvailableTeams: (slot: GridSlotRef) => void;
}

/** The matrix itself: a header row of dates plus one row per declared start time. */
export function GridMatrix({
  model,
  badgeOf,
  pendingMatchIds,
  editDisabled,
  onEditMatch,
  placeholderLabels,
  dragging,
  onAddTime,
  onRemoveTime,
  onShowAvailableTeams,
}: GridMatrixProps) {
  // `dates` and `times` are no longer derived from the same slot list: `times` also folds in
  // `extraTimes`, the rows the organizer added by hand, which never touch `dates`. The guard
  // still holds because `extraTimes` only ever feeds `times` — `dates.length === 0` still means
  // there are no declared slots and no matches at all.
  if (model.dates.length === 0) {
    return (
      <p className="text-sm text-gray-500">
        Este club todavía no tiene franjas declaradas ni partidos para este torneo.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      {/* With six days the grid doesn't fit on screen: the table scrolls, never the body.
          `min-w-full` and NOT `min-w-max`: under `min-w-max` the container is sized to its
          max-content, and a `1fr` track's max-content contribution is its widest cell — so a long
          pair of player names stretched a column far past its 200px floor, `truncate` never
          engaged, and on a phone a single card ended up wider than the screen (which also made the
          drag overlay, sized to that card, spill off the right edge). Against `min-w-full` the
          `1fr` resolves to the container instead: columns share the width when there is room, fall
          back to the 200px minimum when there isn't, and overflow horizontally from there. */}
      <div className="overflow-x-auto">
        <div
          className="grid min-w-full gap-2"
          style={{
            gridTemplateColumns: `72px repeat(${model.dates.length}, minmax(200px, 1fr))`,
          }}
        >
          <div />
          {model.dates.map((date) => (
            <div key={date} className="pb-1 text-center text-sm font-medium">
              {formatDateForUser(date, 'EEE dd/MM')}
            </div>
          ))}

          {model.times.map((time) => (
            <Fragment key={time}>
              <div className="flex items-start justify-end gap-1 pt-2 text-sm font-medium text-gray-600">
                {time}
                {isRemovable(model, time) && (
                  <button
                    type="button"
                    className="rounded border border-gray-200 bg-white p-0.5 text-gray-500 hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                    onClick={() => onRemoveTime(time)}
                    title="Sacar este horario de la grilla"
                    aria-label={`Sacar el horario ${time}`}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
              {model.dates.map((date) => {
                const key = cellKey(date, time);
                const cell = model.cells.get(key);
                // Every intersection of `dates` × `times` gets a cell in `buildGrid`; this guard
                // is only here so the type narrows.
                if (!cell) return null;
                return (
                  <GridCell
                    key={key}
                    cell={cell}
                    badgeOf={badgeOf}
                    pendingMatchIds={pendingMatchIds}
                    editDisabled={editDisabled}
                    onEditMatch={onEditMatch}
                  placeholderLabels={placeholderLabels}
                    dragVerdict={dragging ? dropVerdictFor(dragging, cell) : null}
                    onShowAvailableTeams={onShowAvailableTeams}
                  />
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>

      <GridAddTimeRow onAdd={onAddTime} />
    </div>
  );
}

/**
 * A row can only be dropped if no match sits on it, on any date. Removing a row that holds
 * matches would hide them, which is exactly what the free time axis exists to prevent.
 *
 * A declared row qualifies too: removing it only takes it out of the view (see `hiddenTimes` in
 * `SharedGridView`), the block itself is untouched.
 */
function isRemovable(model: GridModel, time: string): boolean {
  return model.dates.every((date) => {
    const cell = model.cells.get(cellKey(date, time));
    return !cell || cell.matches.length === 0;
  });
}
